/**
 * Quick filters: a row of chips above the collection grid that toggle values
 * of the existing storefront filters (sections/main-collection.liquid,
 * snippets/quick-filters.liquid).
 *
 * The checkboxes in form#facets stay the single source of truth: a chip ticks
 * or unticks them and lets facet-filters.js fetch the results, so the drawer,
 * the active-filter pills and the URL stay in sync. After every update the
 * chips re-read the form (active state, counts, disabled values).
 */
(() => {
  /**
   * Returns the values that should be selected for one filter group after a chip click.
   * @param {string[]} selected - Currently selected values of the chip's group.
   * @param {string[]} chipValues - Values the chip represents.
   * @param {boolean} multi - Allow several chips of the same group at once.
   * @returns {string[]}
   */
  function nextGroupSelection(selected, chipValues, multi) {
    const isActive = chipValues.some((value) => selected.includes(value));
    if (isActive) return selected.filter((value) => !chipValues.includes(value));
    if (!multi) return chipValues.slice();
    return selected.concat(chipValues.filter((value) => !selected.includes(value)));
  }

  /**
   * Checks whether two selections contain the same values.
   * @param {string[]} a - First selection.
   * @param {string[]} b - Second selection.
   * @returns {boolean}
   */
  function sameSelection(a, b) {
    return a.length === b.length && a.every((value) => b.includes(value));
  }

  /**
   * Parses a drawer count label such as '(18)'.
   * @param {string} text - Text of the count element.
   * @returns {?number}
   */
  function parseCount(text) {
    const match = /(\d+)/.exec(text || '');
    return match ? Number(match[1]) : null;
  }

  /**
   * Decides how a chip should look, based on the checkboxes it represents.
   * @param {Array<{checked: boolean, disabled: boolean, count: ?number}|null>} inputs - One
   *   entry per chip value; null when the value is not in the current filters.
   * @returns {{active: boolean, hidden: boolean, count: ?number}}
   */
  function chipState(inputs) {
    const present = inputs.filter(Boolean);
    const active = present.some((input) => input.checked);
    const selectable = present.some((input) => !input.disabled);
    const counts = present.map((input) => input.count);
    const count = counts.length && counts.every((c) => c !== null)
      ? counts.reduce((sum, c) => sum + c, 0)
      : null;

    return { active, hidden: !active && !selectable, count };
  }

  /**
   * Returns the number a chip should show, so it matches the results count after a tap.
   * Drawer counts are per value (Shopify's and stock-filter-counts.js' alike: the
   * value replaces its group's selection), which is what a tap in single mode does.
   * @param {object} opts - Chip situation.
   * @param {?number} opts.count - Sum of the drawer counts of the chip's values.
   * @param {?number} opts.resultsCount - The current results count in the header.
   * @param {boolean} opts.active - The chip is selected.
   * @param {boolean} opts.siblingSelected - Another value of the chip's group is selected.
   * @param {boolean} opts.multi - Several chips of one group may be selected.
   * @returns {?number}
   */
  function shownCount({ count, resultsCount, active, siblingSelected, multi }) {
    // The results on screen are exactly this chip's, unless the drawer added a sibling.
    if (active && !multi && !siblingSelected) return resultsCount;
    return count;
  }

  const core = { nextGroupSelection, sameSelection, parseCount, chipState, shownCount };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = core;
    return;
  }

  if (customElements.get('quick-filters')) return;

  class QuickFilters extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (evt) => this.handleClick(evt));
      this.scheduleSync = this.scheduleSync.bind(this);
      this.handleUpdate = this.handleUpdate.bind(this);
    }

    connectedCallback() {
      this.form = document.getElementById('facets');
      this.facets = document.getElementById('facet-filters');
      this.resultsCount = document.getElementById('results-count');
      this.multi = this.dataset.multi === 'true';
      this.showCounts = this.dataset.counts === 'true';
      this.cacheElements();

      if (!this.form) return;

      if (this.facets) {
        this.facets.addEventListener('on:facet-filters:updated', this.handleUpdate);
        // stock-filter-counts.js rewrites counts, disabled states and the results count
        // after the update event.
        this.observer = new MutationObserver(this.scheduleSync);
        this.observer.observe(this.form, {
          subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled']
        });
        if (this.resultsCount) {
          this.observer.observe(this.resultsCount, { subtree: true, childList: true, characterData: true });
        }
        this.observer.observe(this.facets, { attributes: true, attributeFilter: ['data-stock-counts'] });
      }

      if (this.dataset.sticky === 'true') this.initSticky();

      this.sync();
      this.scrollToActive();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.facets) this.facets.removeEventListener('on:facet-filters:updated', this.handleUpdate);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.stickyMq) this.stickyMq.removeEventListener('change', this.onScroll);
    }

    cacheElements() {
      this.scroller = this.querySelector('.quick-filters__scroller');
      this.chips = Array.from(this.querySelectorAll('.js-quick-filter'));
      this.allChip = this.querySelector('.js-quick-filter-all');
      this.params = QuickFilters.parseList(this.dataset.params);
      if (!this.params.length) this.params = [...new Set(this.chips.map((chip) => chip.dataset.param))];
    }

    /**
     * Re-renders the chips from the document facet-filters.js fetched, because
     * Shopify only lists filter values that have results with the new filters.
     * @param {object} evt - The 'on:facet-filters:updated' event.
     */
    handleUpdate(evt) {
      const fresh = evt && evt.detail && evt.detail.html && evt.detail.html.querySelector('quick-filters');

      if (fresh) {
        const focusKey = QuickFilters.chipKey(document.activeElement, this);
        const scrollLeft = this.scroller ? this.scroller.scrollLeft : 0;

        this.hidden = fresh.hidden;
        this.dataset.params = fresh.dataset.params || '[]';
        this.innerHTML = fresh.innerHTML;
        this.cacheElements();

        if (this.scroller) this.scroller.scrollLeft = scrollLeft;
        if (focusKey) {
          const target = Array.from(this.querySelectorAll('button'))
            .find((button) => QuickFilters.chipKey(button, this) === focusKey);
          if (target && !target.hidden) target.focus({ preventScroll: true });
        }
      }

      this.scheduleSync();
    }

    /**
     * Returns a stable identifier for a chip, to restore focus after a re-render.
     * @param {?Element} el - Element to identify.
     * @param {HTMLElement} root - The quick filters element.
     * @returns {?string}
     */
    static chipKey(el, root) {
      if (!el || !root.contains(el) || el.tagName !== 'BUTTON') return null;
      if (el.classList.contains('js-quick-filters-open')) return 'open';
      if (el.classList.contains('js-quick-filter-all')) return 'all';
      return `${el.dataset.param}|${el.dataset.values}`;
    }

    /**
     * Parses a JSON list from a data attribute.
     * @param {string} json - Attribute value.
     * @returns {string[]}
     */
    static parseList(json) {
      try {
        const values = JSON.parse(json || '[]');
        return Array.isArray(values) ? values.map(String) : [];
      } catch (err) {
        return [];
      }
    }

    /**
     * Returns the filter values of a chip. Liquid hands them over HTML-encoded
     * (Polo's arrives as Polo&#39;s), while the checkbox values are decoded.
     * @param {HTMLElement} chip - Chip button.
     * @returns {string[]}
     */
    static chipValues(chip) {
      return QuickFilters.parseList(chip.dataset.values).map((value) => {
        if (!value.includes('&')) return value;
        const decoder = document.createElement('textarea');
        decoder.innerHTML = value;
        return decoder.value;
      });
    }

    /**
     * Returns the checkboxes of one filter group in the form.
     * @param {string} param - Filter parameter name.
     * @returns {HTMLInputElement[]}
     */
    groupInputs(param) {
      return Array.from(this.form.querySelectorAll('input[type="checkbox"]'))
        .filter((input) => input.name === param);
    }

    /**
     * Returns the selected values of one filter group in the form.
     * @param {string} param - Filter parameter name.
     * @returns {string[]}
     */
    selectedValues(param) {
      return this.groupInputs(param).filter((input) => input.checked).map((input) => input.value);
    }

    /**
     * Ticks exactly the given values of one filter group. A value the server
     * rendered as disabled (no results with the other filters) is enabled first,
     * otherwise FormData would leave it out.
     * @param {string} param - Filter parameter name.
     * @param {string[]} values - Values to select.
     */
    setGroup(param, values) {
      this.groupInputs(param).forEach((input) => {
        const on = values.includes(input.value);
        if (on) input.disabled = false;
        input.checked = on;
      });
    }

    /**
     * Handles clicks on the chips.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const button = evt.target.closest('button');
      if (!button || !this.contains(button) || !this.form) return;

      if (button.classList.contains('js-quick-filters-open')) {
        this.openFilters(button);
        return;
      }

      if (button === this.allChip) {
        if (this.params.every((param) => !this.selectedValues(param).length)) {
          // A filter in the URL that matches nothing isn't in the form, so there is nothing to untick.
          if (window.location.search.includes('filter.') && !document.querySelector('#filter-results ul.grid > li')) {
            window.location.href = window.location.pathname;
          }
          return;
        }
        this.params.forEach((param) => this.setGroup(param, []));
      } else if (button.classList.contains('js-quick-filter')) {
        const { param } = button.dataset;
        const selected = this.selectedValues(param);
        const next = nextGroupSelection(selected, QuickFilters.chipValues(button), this.multi);
        if (sameSelection(selected, next)) return;
        this.setGroup(param, next);
      } else {
        return;
      }

      // apply() first: facet-filters.js marks the results as loading right away,
      // which sync() uses to keep the old header number off the chips.
      this.apply();
      this.sync();
      this.scrollToRowStart();
    }

    /**
     * Opens the filters drawer with the chip as opener, so focus returns to it.
     * @param {HTMLElement} button - The filter chip.
     */
    openFilters(button) {
      const { facets } = this;
      if (facets && facets.showFiltersBtn && typeof facets.open === 'function') {
        facets.open(button);
        return;
      }
      const filterBtn = document.querySelector('.js-show-filters');
      if (filterBtn) filterBtn.click();
    }

    /**
     * Applies the form state through facet-filters.js, or reloads the page when
     * the filters drawer has not been initialised yet.
     */
    apply() {
      const { facets } = this;

      if (facets && facets.form && typeof facets.handleFilterChange === 'function') {
        facets.handleFilterChange({ target: this.form });
        return;
      }

      const searchParams = new URLSearchParams(new FormData(this.form));
      Array.from(searchParams.keys()).forEach((key) => {
        if (!searchParams.get(key)) searchParams.delete(key);
      });
      window.location.search = searchParams.toString();
    }

    /**
     * Batches the many mutations of one update into a single sync. A microtask
     * runs before the next paint (so no frame shows stale counts after
     * stock-filter-counts.js) and also while the page isn't painting (background
     * tab, hidden webview). sync() only touches the chips, which aren't observed.
     */
    scheduleSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      queueMicrotask(() => {
        this.syncQueued = false;
        this.sync();
      });
    }

    /**
     * Updates the chips from the current form state.
     */
    sync() {
      const resultsCount = this.resultsCount ? parseCount(this.resultsCount.textContent) : null;
      // While the new results load, the header still shows the old total.
      const loading = Boolean(this.facets && this.facets.results
        && this.facets.results.classList.contains('is-loading'));
      // Numbers only once stock-filter-counts.js counted cards, like the grid and the header.
      const counted = Boolean(this.facets && this.facets.dataset.stockCounts === 'ok');
      if (counted) this.removeAttribute('data-counts-pending');

      this.chips.forEach((chip) => {
        const values = QuickFilters.chipValues(chip);
        const inputs = values.map((value) => {
          const input = this.groupInputs(chip.dataset.param).find((el) => el.value === value);
          if (!input) return null;
          const countEl = input.parentElement.querySelector('.js-filter-count');
          return {
            checked: input.checked,
            disabled: input.disabled,
            count: countEl ? parseCount(countEl.textContent) : null
          };
        });
        const state = chipState(inputs);

        chip.setAttribute('aria-pressed', state.active);
        chip.hidden = state.hidden;

        const countEl = chip.querySelector('.quick-filters__count');
        // A chip that was just selected keeps its own number until the results arrive.
        if (countEl && this.showCounts && !(loading && state.active)) {
          const count = counted ? shownCount({
            count: state.count,
            resultsCount,
            active: state.active,
            siblingSelected: this.selectedValues(chip.dataset.param).some((value) => !values.includes(value)),
            multi: this.multi
          }) : null;
          countEl.textContent = count === null ? '' : count;
        }
      });

      // A group without visible chips is hidden, so no divider is left behind.
      this.querySelectorAll('.quick-filters__group').forEach((group) => {
        group.hidden = !Array.from(group.querySelectorAll('.js-quick-filter')).some((chip) => !chip.hidden);
      });

      if (this.allChip) {
        const noneSelected = this.params.every((param) => !this.selectedValues(param).length);
        this.allChip.setAttribute('aria-pressed', noneSelected);

        // 'Alles' shows the results count only while it is selected; otherwise
        // the total without the chip filters is unknown here.
        const countEl = this.allChip.querySelector('.quick-filters__count');
        if (countEl && this.showCounts) {
          countEl.textContent = counted && noneSelected && !loading && resultsCount !== null ? resultsCount : '';
        }
      }
    }

    /**
     * Scrolls the row so an active chip is visible, without moving the page.
     */
    scrollToActive() {
      const active = this.scroller && this.scroller.querySelector('.js-quick-filter[aria-pressed="true"]');
      if (!active || this.scroller.scrollWidth <= this.scroller.clientWidth) return;
      this.scroller.scrollLeft = Math.max(0, active.offsetLeft - this.scroller.offsetLeft - 16);
    }

    /**
     * When the row is stuck, scrolls the page back to the start of the results
     * so the new results are seen from the top.
     */
    scrollToRowStart() {
      if (!this.classList.contains('is-stuck')) return;
      const top = parseFloat(getComputedStyle(this).top) || 0;
      const y = this.parentElement.getBoundingClientRect().top + window.scrollY - top;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
    }

    /**
     * Marks the row as stuck while it sits under the sticky header (mobile and tablet).
     */
    initSticky() {
      // Without a sticky header the row sticks to the top of the screen.
      if (!document.querySelector('.header--sticky')) this.style.top = '0px';

      this.stickyMq = window.matchMedia('(max-width: 1023.98px)');
      let queued = false;

      this.onScroll = () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          // An open drawer locks the page (body.fixed) and resets scrollY; keep the state.
          if (document.body.classList.contains('fixed')) return;
          const top = parseFloat(getComputedStyle(this).top) || 0;
          const stuck = this.stickyMq.matches && window.scrollY > 0 && this.getBoundingClientRect().top <= top + 1;
          this.classList.toggle('is-stuck', stuck);
        });
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.stickyMq.addEventListener('change', this.onScroll);
      this.onScroll();
    }
  }

  customElements.define('quick-filters', QuickFilters);
})();
